import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"
import summaryAccountService from "./summaryaccount"
import validationService from "data/validate"

type DetailAccount = {
  detailaccountid: number
}
// private module logic //

const id: string = "detailAccount"
const initialState: ApiResult<DetailAccount[]> = { id, state: "init", value: [] }
const detailAccountStream = new BehaviorSubject<ApiResult<DetailAccount[]>>(initialState)
const detailAccountEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let detailAccountMap = {}
const _patches_ = PromiseWatch()

async function sortData(data: DetailAccount[], chartid: number): Promise<DetailAccount[]> {
  let summaries = await summaryAccountService.fetchSummaryAccounts(chartid) as [{accountnumber: string, summaryaccountid: number}]
  summaries.sort((a, b) => alphanum(_.get(a, "accountnumber"), _.get(b, "accountnumber")))

  data.sort((a, b) => {
    let A = a["summaryaccountid"], A2 = a["accountnumber"], B = b["summaryaccountid"], B2 = b["accountnumber"]
    let Ai = summaries.findIndex(item => item.summaryaccountid === A), Bi = summaries.findIndex(item => item.summaryaccountid === B)

    if(Ai > Bi) return 1
    else if(Ai < Bi) return -1

    if(A2 > B2) return 1
    else if(A2 < B2) return -1
    else return 0
  });

  return data
}

function clear(): void {
  detailAccountStream.next({ id, state: 'ok', value: [] })
  detailAccountMap = {}
}

function get(detailAccountMapId: number): DetailAccount {
  return detailAccountMap[detailAccountMapId]
}

function fetchDetailAccounts(chartid:number): Promise<DetailAccount[] | { message: string }> {
  clear()

  return Client.get(`detailaccount?chartid=eq.${chartid}`)
  .then(async (records: DetailAccount[]) => {
    detailAccountStream.next({ id, state: 'ok', value: await sortData(records, chartid) })
    validationService.fetchByParent('detailaccount', 'chart', chartid)
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function create(chartid:number): Promise<DetailAccount | { message: string }> {
  const streamVal = detailAccountStream.getValue().value
  detailAccountStream.next({ id, state: 'busy', value: streamVal })

  const payload = {
    chartid,
    status: "Active"
  }

  return Client.post(`detailaccount`, payload)
    .then((detailAccount: DetailAccount) => {
      push_undo({
        undofunc: () => deleteOne(chartid, detailAccount.detailaccountid),
        redofunc: () => create(chartid)
      })
      detailAccountMap[detailAccount.detailaccountid] = detailAccount
      // merge results unique by detailaccountid
      const streamVal = detailAccountStream.getValue().value
      detailAccountStream.next({
        id,
        state: 'ok',
        value: _.unionBy([detailAccount], streamVal, 'detailaccountid')
      })
      detailAccountEventStream.next({ type: 'DETAILACCOUNT_CREATED', payload: detailAccount })
      validationService.fetchByParent('detailaccount', 'chart', chartid)
      return detailAccount
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      detailAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

// function copy(detailaccountid:number, chartid:number) {
//   const streamVal = detailAccountStream.getValue().value;
//   detailAccountStream.next({ id, state: 'busy', value: streamVal });

//   const payload = {
//     table: 'detailaccount',
//     recordid: detailaccountid,
//     _synchronous: true,
//     code: incrementUnique(code, "code", streamVal),
//     name: incrementUnique(name, "name", streamVal)
//   };

//   Client.post(`msg/svc_recordcopy`, payload)
//     .then((detailAccount: DetailAccount) => {
//       const newRecord = detailAccount[0]
//       push_undo({
//         undofunc: () => deleteOne(newRecord.detailaccountid),
//         redofunc: () => copy(detailaccountid, code, name)
//       });
//       detailAccountMap[newRecord.detailaccountid] = newRecord
//       // merge results unique by detailaccountid
//       const streamVal = detailAccountStream.getValue().value
//       detailAccountStream.next({
//         id,
//         state: 'ok',
//         value: _.unionBy([newRecord], streamVal, 'detailaccountid')
//       })
//       detailAccountEventStream.next({ type: 'DETAILACCOUNT_COPIED', payload: newRecord })
//       validationService.fetchByParent('detailaccount', 'chart', chartid)
//       return newRecord
//     })
//     .catch((err: { error: {text: string }}) => {
//       // on error overwrite optimistic update
//       detailAccountStream.next({ id, state: 'error', error: err, value: streamVal })
//       notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
//       return err
//     })
// }

async function deleteOne(
  chartid: number,
  detailaccountid: number
): Promise<boolean | { message: string }> {
  const streamVal = detailAccountStream.getValue().value

  const deleteFn = () => deleteOne(chartid, detailaccountid)
  const refreshFn = () => fetchDetailAccounts(chartid)
  if (!(await handleReferences('detailaccount', detailaccountid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted detailaccountid
  const value = _(streamVal)
    .filter((p) => p.detailaccountid !== detailaccountid)
    .value()
  value.forEach((p) => (detailAccountMap[p.detailaccountid] = p))

  detailAccountStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(detailaccountid).then(() =>
  Client.del(`detailaccount?detailaccountid=eq.${detailaccountid}`)
    .then((detailAccount: DetailAccount) => {
      delete detailAccountMap[detailAccount.detailaccountid]
      push_undo({
        undofunc: () => {
          Client.post('detailAccount', detailAccount)
          detailAccountMap[detailAccount.detailaccountid] = detailAccount
          detailAccountStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(chartid, detailaccountid)
      })
      validationService.fetchByParent('detailaccount', 'chart', chartid)
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      detailAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchDetailAccounts(chartid)
      return err
    })
  )
}

async function deleteMultiple(
  chartid: number,
  detailaccountids: number[]
): Promise<boolean | { message: string }> {
  const streamVal = detailAccountStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(chartid, ids)
  const refreshFn = () => fetchDetailAccounts(chartid)
  if (!(await handleReferences('detailaccount', detailaccountids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted detailaccountid
  const value = _(streamVal)
    .filter((p) => !detailaccountids.includes(p.detailaccountid))
    .value()
  value.forEach((p) => (detailAccountMap[p.detailaccountid] = p))

  detailAccountStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(detailaccountids).then(() =>
  Client.del(`detailaccount?detailaccountid=in.${detailaccountids}`)
    .then((detailAccounts: DetailAccount[]) => {
      detailAccounts.forEach(d => delete detailAccountMap[d.detailaccountid])
      push_undo({
        undofunc: () => {
          Client.post('detailAccount', detailAccounts)
          detailAccounts.forEach(d => detailAccountMap[d.detailaccountid] = d)
          detailAccountStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteMultiple(chartid, detailaccountids)
      })
      validationService.fetchByParent('detailaccount', 'chart', chartid)
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      detailAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchDetailAccounts(chartid)
      return err
    })
  )
}

function patch(detailAccount: {
  detailaccountid: number, chartid: number
}): Promise<DetailAccount | { message: string }> {
  const streamVal = detailAccountStream.getValue().value
  const detailaccountid: number = detailAccount.detailaccountid
  const streamValdetailAccount = _.find(streamVal, { detailaccountid })
  const changeset = diff_obj(detailAccount, streamValdetailAccount)
  if (_.isEmpty(changeset)) return new Promise(() => streamVal)
  push_undo({
    undofunc: () => patch(streamValdetailAccount),
    redofunc: () => patch(detailAccount)
  })

  const newStreamVal = _.cloneDeep(streamVal)
  newStreamVal.splice(_.findIndex(streamVal, a => a.detailaccountid === detailaccountid), 1, detailAccount)

  // optimistic update
  detailAccountStream.next({
    id,
    state: 'ok',
    value: newStreamVal
  })

  // convert propertytype array to postgres_ style array e.g. {x,y,z}
  if(changeset.propertytype && _.isArray(changeset.propertytype)) {
    changeset.propertytype = `{${_.join(changeset.propertytype,',')}}`
  }

  return _patches_.watch(detailaccountid, Client.patch(`detailaccount?detailaccountid=eq.${detailaccountid}`, changeset)
    .then((detailAccount: {detailaccountid: number}) => {
      detailAccountMap[detailAccount.detailaccountid] = detailAccount
      validationService.fetch('detailaccount', detailaccountid)
      return detailAccount
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      detailAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchDetailAccounts(detailAccount.chartid)
      return err
    }))

}

// public module interface //

const detailAccountService = {
  stream: () => detailAccountStream.asObservable(),
  value: () => detailAccountStream.getValue().value,
  eventStream: () => detailAccountEventStream.asObservable(),
  clear,
  get,
  //fetch,
  fetchDetailAccounts,
  create,
  //copy,
  deleteOne,
  deleteMultiple,
  patch
}

export default detailAccountService
